import type { Job } from '@/domains/jobs/components/JobCard';
import { differenceInDays, parseISO } from 'date-fns';
import type { GanttZoomLevel } from '../toolbar/types';

const MAX_LANES = 10;
const MS_PER_HOUR = 1000 * 60 * 60;

export interface GanttLayoutOptions {
  rangeStart: Date;
  pixelsPerDay: number;
  totalDays: number;
  zoomLevel: GanttZoomLevel;
}

export interface GanttJobBarLayout {
  job: Job;
  left: number;
  width: number;
  lane: number;
}

export interface GanttJobLayout {
  bars: GanttJobBarLayout[];
  /** Lanes in use; minimum 1 so an empty row keeps its baseline height. */
  laneCount: number;
}

interface PositionedBar {
  left: number;
  width: number;
  startDay: number;
  endDay: number;
}

function positionBar(job: Job, options: GanttLayoutOptions): PositionedBar | null {
  const { rangeStart, pixelsPerDay, totalDays, zoomLevel } = options;
  if (!job.scheduledDate) return null;

  if (zoomLevel === 'hour' && job.startTime && job.endTime) {
    const jobStart = parseISO(job.startTime);
    const jobEnd = parseISO(job.endTime);
    const hoursFromStart = (jobStart.getTime() - rangeStart.getTime()) / MS_PER_HOUR;
    const jobDurationHours = (jobEnd.getTime() - jobStart.getTime()) / MS_PER_HOUR;

    // End-exclusive, except a zero-length booking sitting exactly on the range start.
    const endMs = jobEnd.getTime();
    const rangeStartMs = rangeStart.getTime();
    const endsBeforeRange =
      endMs < rangeStartMs || (endMs === rangeStartMs && jobDurationHours > 0);
    if (endsBeforeRange || hoursFromStart > totalDays * 24) return null;

    const pixelsPerHour = pixelsPerDay / 24;
    return {
      left: Math.max(0, hoursFromStart * pixelsPerHour),
      width: Math.max(pixelsPerHour * 0.5, jobDurationHours * pixelsPerHour),
      startDay: hoursFromStart / 24,
      endDay: (hoursFromStart + jobDurationHours) / 24,
    };
  }

  const jobStart = parseISO(job.scheduledDate);
  const jobEnd = job.scheduledEndDate ? parseISO(job.scheduledEndDate) : jobStart;
  const daysFromStart = differenceInDays(jobStart, rangeStart);
  const jobDuration = differenceInDays(jobEnd, jobStart) + 1;

  if (daysFromStart + jobDuration <= 0 || daysFromStart > totalDays) return null;

  return {
    left: Math.max(0, daysFromStart * pixelsPerDay),
    width: jobDuration * pixelsPerDay,
    startDay: daysFromStart,
    endDay: daysFromStart + jobDuration,
  };
}

export function layoutGanttJobBars(jobs: Job[], options: GanttLayoutOptions): GanttJobLayout {
  const lanes: { startDay: number; endDay: number }[][] = [];
  const bars: GanttJobBarLayout[] = [];
  let maxLane = 0;

  for (const job of jobs) {
    const positioned = positionBar(job, options);
    if (!positioned) continue;

    let lane = 0;
    for (let l = 0; l < MAX_LANES; l++) {
      if (!lanes[l]) lanes[l] = [];
      const hasOverlap = lanes[l].some(
        (occupied) =>
          positioned.startDay < occupied.endDay && positioned.endDay > occupied.startDay,
      );
      if (!hasOverlap) {
        lane = l;
        lanes[l].push({ startDay: positioned.startDay, endDay: positioned.endDay });
        break;
      }
    }

    maxLane = Math.max(maxLane, lane);
    bars.push({ job, left: positioned.left, width: positioned.width, lane });
  }

  return { bars, laneCount: maxLane + 1 };
}
